import { useMemo, useState } from "react";
import { useLogoStore } from "../state/logoStore";
import { renderLogoSVG } from "../engine/render";
import { generateIdeas, ideaToStatePatch, type DesignIdea } from "../engine/ideaGenerator";
import { PageLabel } from "../components/ui/PageLabel";

// === PAGE: GENERATED (auto-generated 20 random icon-with-container compositions) ===
export function GeneratedPage() {
  const s = useLogoStore();
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 100000));

  const ideas = useMemo(
    () => generateIdeas({
      title: s.title || "Your Brand",
      slogan: s.slogan,
      referencePalette: s.referencePalette,
      customIcons: s.customIcons,
      preferIconId: null,    // explore widely — don't bias around current icon
      count: 20,
      seed,
    }),
    [s.title, s.slogan, s.referencePalette, s.customIcons, seed]
  );

  function applyAndEdit(idea: DesignIdea) {
    s.pushHistory();
    s.setStyle(ideaToStatePatch(idea) as Parameters<typeof s.setStyle>[0]);
    s.setIcon(idea.iconId);
    s.setPage("editor");
    window.scrollTo({ top: 0 });
  }

  return (
    <div className="page generated">
      <PageLabel name="GENERATED · 20 random containers" />
      <header className="topbar">
        <button className="btn pill-nav pill-nav-back" onClick={() => s.setPage("start")}>
          <span className="pill-nav-arrow" aria-hidden="true">←</span>
          <span>Home</span>
        </button>
        <h2>Pick a starting container · {ideas.length} ideas</h2>
        <button
          className="btn primary"
          onClick={() => setSeed((v) => v + 1)}
          title="Generate a fresh batch of 20"
        >↻ Generate 20 more</button>
      </header>

      <div style={{ padding: "20px 24px 8px" }}>
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5, maxWidth: 720 }}>
          Each tile mixes a different icon, layout, font pair, container, pattern, and palette — randomly combined and scored for balance.
          Tap any container to apply it and jump into the editor for fine-tuning.
        </div>
      </div>

      <div className="ideas-grid generated-grid">
        {ideas.map((idea, i) => {
          const svg = renderLogoSVG(
            { title: s.title || "Your Brand", slogan: s.slogan || "", ...idea, qrText: null } as Parameters<typeof renderLogoSVG>[0],
            { width: 320, height: 320, customIcons: s.customIcons }
          );
          return (
            <button
              key={i}
              className="idea-card"
              onClick={() => applyAndEdit(idea)}
              title={`Use this · score ${idea.score.scorePercent}/100 (${idea.score.grade})`}
            >
              <div className="idea-svg" dangerouslySetInnerHTML={{ __html: svg }} />
              <div className={`idea-grade grade-${gradeClass(idea.score.grade)}`}>
                {idea.score.grade}<span> {idea.score.scorePercent}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ padding: "16px 24px 32px", textAlign: "center" }}>
        <button className="btn primary big" onClick={() => setSeed((v) => v + 1)}>
          ↻ Generate 20 more
        </button>
      </div>
    </div>
  );
}

function gradeClass(grade: string): string {
  if (grade === "A+" || grade === "A") return "a";
  if (grade === "B") return "b";
  if (grade === "C") return "c";
  if (grade === "D") return "d";
  return "f";
}
